import React, { useState, useMemo } from "react";
import { Search, X, Plus } from "lucide-react";
import PolicyForm from "../../components/Form/PolicyForm";
import ReusableTable from "../../components/UI/ReusableTable";
import ReactPaginate from "react-paginate";

const AddPolicy = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [message, setMessage] = useState("");

  // Example Pagination config
  const itemsPerPage = 10;

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const openModalForAdd = () => {
    setIsModalOpen(true);
    setMessage("");
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleFormSubmit = (formData) => {
    // Add logic to save policy
    setPolicies([
      ...policies,
      {
        id: Math.random(),
        leave_code: formData.leave_code || "-",
        leave_name: formData.leave_name || "-",
        effective_date: formData.effective_date || "-",
        applies_to: formData.applies_to || "-",
      },
    ]);
    setMessage("Policy saved successfully!");
    closeModal();
  };

  const filteredPolicies = useMemo(() => {
    return policies.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return (
        item.leave_code?.toLowerCase().includes(searchTerm) ||
        item.leave_name?.toLowerCase().includes(searchTerm)
      );
    });
  }, [policies, searchQuery]);

  const columns = [
    { label: "Leave Code", field: "leave_code" },
    { label: "Leave Name", field: "leave_name" },
    { label: "Effective Date", field: "effective_date" },
    { label: "Applies To", field: "applies_to" },
  ];

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">
          Add Policy
        </h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by Leave Code or Name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Add Button */}
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap flex items-center"
        >
          <Plus size={16} className="mr-2" />
          Add Policy
        </button>
      </div>

      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      <div>
        <ReusableTable
          data={filteredPolicies}
          loading={false}
          columns={columns}
          filterable={false}
          excludeFilterColumns={[]}
        />
      </div>

      <ReactPaginate
        breakLabel="..."
        nextLabel="Next >"
        onPageChange={(event) => handlePageChange(event.selected + 1)}
        forcePage={currentPage - 1}
        pageRangeDisplayed={5}
        pageCount={Math.ceil(filteredPolicies.length / itemsPerPage) || 1}
        disabledClassName='opacity-50 cursor-not-allowed'
        disabledLinkClassName='pointer-events-none'
        previousLabel="< Prev"
        previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
        previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
        nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
        nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
        renderOnZeroPageCount={null}
        containerClassName="flex items-center justify-center mt-4 font-poppins"
        pageClassName="mx-1 border rounded dark:text-white font-poppins"
        pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
        activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
        activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
      />

      {isModalOpen && (
        <div className="fixed inset-0 z-[1005] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm transition-opacity">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col overflow-hidden max-h-[90vh]">
            <div className="px-6 py-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                Add Policy
              </h2>
              <button
                onClick={closeModal}
                className="p-2 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 relative bg-white dark:bg-gray-900">
              <PolicyForm
                closeModal={closeModal}
                onSubmit={handleFormSubmit}
                initialValues={{}}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddPolicy;
